import * as React from "react";

import { useDebouncedCallback } from "use-debounce";
import { useCombobox, UseComboboxStateChange } from "downshift";

import { isMobile } from "../../../constants/browser";

interface DropdownClasses {
    inputLabel?: string;
    inputContainer?: string;
    inputContainerOpened?: string;
    inputContainerError?: string;
    input?: string;
    inputClearButton?: string;
    menu?: string;
    menuOpened?: string;
    menuContainer?: string;
    menuTitle?: string;
    menuList?: string;
    listItem?: string;
    listItemText?: string;
    listItemSelected?: string;
    emptyResults?: string;
}

interface IndexedObject {
    label: string;
    value: string;
    [key: string]: string | number;
}

interface DropdownProviderProps {
    /**
     * Classes for styling html elements
     */
     classes?: DropdownClasses;
    /**
     * List of values to provide to menu
     */
     dropdownList: IndexedObject[];
    /**
     * Optional callback for debouncing
     */
     debounceWait?: number;
    /**
     * ID for the input field
     */
     inputId: string;
    /**
     * Type used for input field, defaults to search
     */
     inputType?: "search" | "text"
     /**
      * Name for the input field
      */
     inputName?: string;
    /**
      * Optional boolean field used to add validation behavior
      */
     invalidInput?: boolean;
    /**
     * Optional initial value to display
     */
     fieldValue?: IndexedObject;
    /**
     * Optional max length of the input
     */
     maxLength?: number;
    /**
     * Callback to run additional logic on input change
     */
     onInputChange?: (changes: UseComboboxStateChange<IndexedObject>) => void;
     /**
      * Callback to run additional logic on menu toggle
      */
     onMenuToggle?: (value: boolean) => void;
     /**
      * Text displayed in input as a placeholder
      */
     placeHolderText?: string;
     /**
      * Value for input field
      */
     selectedItem?: IndexedObject;
     /**
      * On initial focus of input, display menu.
      * Default behavior is to only appear after input value is updated
      */
     showMenuOnFocus?: boolean;
     /**
      * Controls whether the clear behavior refocuses the input on mobile
      */
     refocusMobileOnClear?: boolean;
     /**
      * Override the selected value
      */
     clearValue?: boolean;
     /**
      * Optional callback for when the input is cleared
      */
     onClear?: () => void;
     /**
      * Controls whether the menu will close if there is no input value
      */
     onMouseSelectItem?: (event: React.MouseEvent<HTMLInputElement>, selectedItem: IndexedObject) => void;
     onKeyboardSelectItem?: (event: React.KeyboardEvent<HTMLInputElement>, selectedItem: IndexedObject) => void;
    closeMenuIfInputEmpty?: boolean;
    value?: string | null
}

const DropdownContext = React.createContext<any>({});

/**
 * Provider for dropdown behaviors derived from downshift.
 * Wrap all dropdown subcomponents with this provider.
 */
const DropdownProvider = (props: React.PropsWithChildren<DropdownProviderProps>) => {
    const {
        classes, dropdownList, debounceWait, inputId, inputName, invalidInput = false, fieldValue, inputType = "search",
        maxLength, onInputChange, onMenuToggle, placeHolderText, selectedItem, showMenuOnFocus, onMouseSelectItem,
        onKeyboardSelectItem, refocusMobileOnClear = false, clearValue, onClear, closeMenuIfInputEmpty = false, children
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);
    const labelRef = React.useRef<HTMLLabelElement>(null);
    const labelText = labelRef.current ? labelRef.current.textContent : "";

    const focusInput = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const processValueChange = async (changes: UseComboboxStateChange<IndexedObject>) => {
        if (isMobile()) {
            // When an item is clicked, triggering blur() on the current input will close the menu.
            if (changes.type === useCombobox.stateChangeTypes.ItemClick) {
                inputRef?.current?.blur();
            }
        }

        if (!changes) {
            return;
        }

        onInputChange?.(changes);
    };

    const debouncedProcessValueChange = useDebouncedCallback(
        (changes: UseComboboxStateChange<IndexedObject>) => {
            processValueChange(changes);
        },
        debounceWait
    );

    const onInputValueChange = async (changes: UseComboboxStateChange<IndexedObject>) => {
        if (debounceWait) {
            debouncedProcessValueChange(changes);
        } else {
            processValueChange(changes);
        }
    };

    // Get values derived from Downshift
    const {
        isOpen,
        getLabelProps,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        highlightedIndex,
        openMenu,
        closeMenu,
        getItemProps,
        reset,
        inputValue,
        selectItem
    } = useCombobox({
        selectedItem,
        id: inputId,
        items: dropdownList,
        itemToString: (item: IndexedObject | null) => (item ? item.label : ""),
        onInputValueChange,
        // eslint-disable-next-line no-shadow
        onIsOpenChange: ({ isOpen }) => {
            if (onMenuToggle && typeof isOpen === "boolean") {
                if (!isOpen) {
                    setTimeout(() => {
                        onMenuToggle(isOpen);
                    }, 75);
                    return;
                }
                onMenuToggle(isOpen);
            }
        }
    });

    const clearInput = React.useCallback(() => {
        reset();
        onClear?.();
    }, [onClear, reset]);

    const onClearButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (!refocusMobileOnClear && isMobile()) {
            event.stopPropagation();
            clearInput();
            openMenu();
        } else {
            clearInput();
            focusInput();
            openMenu();
        }
    };

    const inputProps = getInputProps({
        onFocus: () => {
            if (!isOpen && showMenuOnFocus) {
                openMenu();
            }
        },
        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                // Prevent Downshift's default 'Enter' behavior when no item is selected in list.
                if (highlightedIndex === -1) {
                    // eslint-disable-next-line no-param-reassign
                    (event.nativeEvent as any).preventDownshiftDefault = true;
                    closeMenu();
                }
                if (onKeyboardSelectItem) {
                    onKeyboardSelectItem(event, dropdownList[highlightedIndex]);
                }
            }
        },
        ref: inputRef,
        placeholder: placeHolderText,
        name: inputName || undefined,
        maxLength
    });

    React.useEffect(() => {
        if (clearValue) {
            clearInput();
        }
    }, [clearInput, clearValue]);

    React.useEffect(() => {
        if (fieldValue) {
            selectItem(fieldValue);
        }
    }, [fieldValue, selectItem]);

    React.useEffect(() => {
        if (closeMenuIfInputEmpty && !inputValue && (refocusMobileOnClear && !isMobile())) {
            closeMenu();
        }
    }, [closeMenu, closeMenuIfInputEmpty, inputValue, refocusMobileOnClear]);

    return (
        <DropdownContext.Provider value={{
            // Props generated by Downshift
            highlightedIndex,
            inputValue,
            isOpen,
            comboBoxProps: getComboboxProps(),
            inputProps,
            labelProps: getLabelProps(),
            menuProps: getMenuProps(),
            itemProps: dropdownList.map((item, index) => getItemProps({
                item,
                index,
                onClick: (event: React.MouseEvent<HTMLInputElement>) => {
                    if (onMouseSelectItem) {
                        onMouseSelectItem(event, dropdownList[highlightedIndex]);
                    }
                },
            })),
            openMenu,
            closeMenu,
            reset,

            // Non-Downshift dropdown props
            classes,
            inputType,
            labelRef,
            labelText,
            invalidInput,
            onClearButtonClick,
            focusInput,
            dropdownItems: dropdownList
        }}
        >
            {children}
        </DropdownContext.Provider>
    );
};

export {
    DropdownProvider,
    DropdownContext,
};

export type {
    DropdownProviderProps,
    DropdownClasses,
    IndexedObject
};
